import { useCallback, useEffect, useState } from 'react';
import MyApiClient from '../../../../api-client/MyApiClient';
import { nomiSport, sportDaSalvare, sportDelProdotto } from '../../../components/Util/sportProdotto';
import { generaDateRegola, giornoDellaData, regolaSettimanaleDa } from './pianificazione';
import { numeroDaTesto, prezzoPerCampo } from "../../../components/Util/prezzi";

/*
 * Corsi — il corso vero del gestionale, letto e scritto attraverso le API.
 *
 * La scheda corso mette insieme cose che sul server stanno in posti diversi: il corso, le sue
 * squadre, le sedute a calendario di ogni squadra, i membri di ogni squadra e gli iscritti al
 * corso. Qui si leggono tutte insieme e si compongono nella forma che serve alla pagina; le
 * azioni scrivono e poi si rilegge, cosi' quello che si vede e' sempre quello che c'e'.
 */

const gettone = () => JSON.parse(sessionStorage.getItem('userDetails') || '{}').idToken;

const dueCifre = (n) => String(n).padStart(2, '0');
const giornoLocale = (d) => `${d.getFullYear()}-${dueCifre(d.getMonth() + 1)}-${dueCifre(d.getDate())}`;
const oraLocale = (d) => `${dueCifre(d.getHours())}:${dueCifre(d.getMinutes())}`;

/** Una seduta del server diventa una data della pianificazione. */
export const dataDaSeduta = (seduta) => {
    const inizio = new Date(seduta.startDateTime);
    const fine = new Date(seduta.endDateTime);
    const data = seduta.bookingDate || giornoLocale(inizio);
    return {
        id: String(seduta.id),
        data,
        giorno: giornoDellaData(data),
        inizio: seduta.startTime || oraLocale(inizio),
        fine: seduta.endTime || oraLocale(fine),
        maestroId: seduta.coachId || '',
        impiantoId: seduta.venueId || '',
    };
};

const statoSquadra = (stato) => (stato === 'ACTIVE' ? 'Attiva' : stato === 'INACTIVE' ? 'Non attiva' : 'Bozza');
const statoSquadraServer = (stato) => (stato === 'Attiva' ? 'ACTIVE' : stato === 'Non attiva' ? 'INACTIVE' : 'DRAFT');

const elenco = (risposta) => (Array.isArray(risposta) ? risposta : Array.isArray(risposta?.data) ? risposta.data : []);

/** Il corso con tutto quello che gli gira intorno, pronto per la pagina. */
const leggiCorso = async (id) => {
    const token = gettone();
    if (!token) throw new Error('sessione scaduta');
    const apiClient = new MyApiClient();

    const [vero, iscritti, squadreVere] = await Promise.all([
        apiClient.getBookingCourseById(token, id),
        apiClient.getUserBookingCourses(token, id),
        apiClient.getBookingCourseTeams(token, id),
    ]);
    if (!vero || vero.error) throw new Error(vero?.error || 'corso non trovato');

    // Di ogni squadra servono le sedute a calendario e chi ne fa parte
    const squadre = await Promise.all(elenco(squadreVere).map(async (t) => {
        const [sedute, membri] = await Promise.all([
            apiClient.getTeamSlotBookings(token, t.id),
            apiClient.getBookingCourseTeamMembers(token, t.id),
        ]);
        const stato = statoSquadra(t.status);
        const dal = (t.startDate || vero.startDate || '').slice(0, 10);
        const al = (t.endDate || vero.endDate || '').slice(0, 10);
        const regola = t.weeklySchedule || [];
        const aCalendario = elenco(sedute)
            .map(dataDaSeduta)
            .sort((a, b) => (a.data + a.inizio).localeCompare(b.data + b.inizio));

        // Una bozza non ha sedute: le sue date nascono quando la si attiva. Fino ad allora
        // si mostra quello che la regola prevede, se no la squadra sembra vuota anche
        // quando il giorno e l'orario li hai gia' scelti.
        const previste = regola.flatMap(r => generaDateRegola(
            { giorno: r.dayOfWeek, inizio: r.startTime, fine: r.endTime, maestroId: t.coachId, impiantoId: t.venueId },
            dal,
            al,
        )).sort((a, b) => (a.data + a.inizio).localeCompare(b.data + b.inizio));

        return {
            id: String(t.id),
            nome: t.name || 'Squadra',
            maestroId: t.coachId || t.coach?.id || '',
            impiantoId: t.venueId || t.venue?.id || '',
            maxMembri: t.maxMembers || 0,
            dal,
            al,
            stato,
            regola,
            membri: elenco(membri).map(m => String(m.userId || m.id)),
            date: aCalendario.length > 0 || stato !== 'Bozza' ? aCalendario : previste,
        };
    }));

    const squadraDi = (userId) => squadre.find(s => s.membri.includes(String(userId)))?.id || null;

    return {
        id: vero.id,
        nome: vero.name,
        // Gli sport del corso (il primo e' il principale) e i loro nomi, da mostrare
        sport: nomiSport(vero),
        sportTypes: sportDelProdotto(vero),
        // OPEN / CLOSED: il server lo vuole per attivare il corso.
        tipo: vero.type || 'OPEN',
        stato: vero.status === 'PURCHASABLE' ? 'Attivo' : vero.status === 'CANCELLED' ? 'Non attivo' : 'Bozza',
        // Archivio (TASK #7, 23/10/2026): un Non attivo messo in archivio dalla tendina dello stato
        archiviato: !!vero.archived,
        dal: (vero.startDate || '').slice(0, 10),
        al: (vero.endDate || '').slice(0, 10),
        maxPartecipanti: vero.maxMembers || 0,
        descrizione: vero.description || '',
        prezzoStandard: prezzoPerCampo(vero.standard_price || 0),
        // L'immagine del corso (indirizzo firmato, solo per vederla); quella nuova va in nuovaFoto.
        foto: vero.coursePicture || '',
        vat_rate: vero.vat_rate,
        vat_nature: vero.vat_nature,
        iva_5_percent: vero.iva_5_percent,
        prezzoTesserati: prezzoPerCampo(vero.federated_price || 0),
        cashback: vero.fitcoin_cashback || 0,
        pagaInStruttura: !!vero.payable_in_club,
        pagaStripe: !!vero.payable_stripe,
        pagaBonifico: !!vero.payable_bank,
        // «Non pubblicare in app» (TASK #9, 21/10/2026)
        nonInApp: !!vero.hidden_in_app,
        squadre,
        // Un'iscrizione annullata («Rimuovi dal corso») non è più un partecipante:
        // senza questo filtro restava in elenco e nei contatori.
        partecipanti: elenco(iscritti).filter(u => u.status !== 'CANCELLED').map(u => {
            const userId = String(u.juniorId || u.playerId || '');
            return {
                id: String(u.id),
                userId,
                nome: u.juniorName || u.playerName || 'Senza nome',
                email: u.playerEmail || '',
                telefono: u.playerPhoneNumber || '',
                acquisto: u.payment_method || '',
                pagato: u.status === 'PAID',
                squadraId: squadraDi(userId),
            };
        }),
    };
};

/** Il corso della pagina: si legge all'apertura e si rilegge dopo ogni scrittura. */
export const useCorso = (id) => {
    const [corso, setCorso] = useState(null);
    const [caricando, setCaricando] = useState(true);
    const [errore, setErrore] = useState(null);

    const ricarica = useCallback(async () => {
        setCaricando(true);
        try {
            setCorso(await leggiCorso(id));
            setErrore(null);
        } catch (e) {
            console.error('Corsi: corso non letto dal gestionale', e);
            setErrore(e?.message || 'corso non letto');
        } finally {
            setCaricando(false);
        }
    }, [id]);

    useEffect(() => { ricarica(); }, [ricarica]);

    return { corso, caricando, errore, ricarica };
};

/*
 * Le scritture. Ognuna restituisce null se è andata, oppure il motivo in chiaro: la pagina lo
 * mostra e rilegge il corso.
 */

/** Il motivo del rifiuto, se c'è: le chiamate del client rispondono con { error } quando va male. */
const motivo = (risposta, fallback) => {
    if (risposta && risposta.error) return risposta.error;
    if (risposta === false) return fallback;
    return null;
};

const statoCorsoServer = (stato) => (stato === 'Attivo' ? 'PURCHASABLE' : stato === 'Non attivo' ? 'CANCELLED' : 'DRAFT');

/**
 * Salva la scheda del corso.
 *
 * Lo stato non si cambia da qui: il server, su un corso gia' pubblicato, tiene quello che
 * ha e ignora in silenzio quello che gli mandi - si leggeva «salvato correttamente» e il
 * corso restava attivo. Il cambio di stato ha una sua strada, che e' anche l'unica a fare
 * i controlli dell'attivazione, e si percorre prima di salvare il resto.
 */
export const salvaCorso = async (corso, statoOriginale = corso.stato) => {
    const token = gettone();
    const apiClient = new MyApiClient();
    // Il server vuole la struttura anche in modifica: senza clubId ogni
    // salvataggio della scheda veniva rifiutato.
    const utente = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
    const risposta = await apiClient.updateBookingCourse(token, {
        clubId: utente.localId,
        id: corso.id,
        name: corso.nome,
        ...sportDaSalvare(corso.sportTypes),
        type: corso.tipo || 'OPEN',
        maxMembers: parseInt(corso.maxPartecipanti, 10) || 0,
        description: corso.descrizione || '',
        standard_price: numeroDaTesto(corso.prezzoStandard) || 0,
        federated_price: numeroDaTesto(corso.prezzoTesserati) || 0,
        fitcoin_cashback: numeroDaTesto(corso.cashback) || 0,
        payable_in_club: !!corso.pagaInStruttura,
        payable_stripe: !!corso.pagaStripe,
        payable_bank: !!corso.pagaBonifico,
        hidden_in_app: !!corso.nonInApp,
        vat_rate: corso.vat_rate ?? 22,
        // Senza natura (aliquote 5 e 22) il campo non si manda: vuoto il server lo rifiuta.
        ...(corso.vat_nature ? { vat_nature: corso.vat_nature } : {}),
        iva_5_percent: corso.vat_rate === 5,
        ...(corso.nuovaFoto ? {
            newCoursePicture: {
                content: corso.nuovaFoto,
                name: Math.random().toString(36).substring(2, 10) + (new Date()).getTime().toString(36),
            },
        } : {}),
        // Lo stato si manda com'e' adesso: a cambiarlo ci pensa la chiamata qui sotto
        status: statoCorsoServer(statoOriginale),
    });
    if (risposta?.error) return risposta.error;

    if (corso.stato !== statoOriginale) {
        const cambio = await apiClient.changeCourseStatus(token, corso.id, statoCorsoServer(corso.stato));
        if (cambio?.error) return cambio.error;
        if (cambio === false) return 'cambio di stato non riuscito';
    }

    return null;
};

/**
 * Crea la squadra sul gestionale: la regola settimanale diventa `weeklySchedule` e l'attivazione
 * fa generare le sedute al server. Le date che si discostano dalla regola (orario, maestro o
 * impianto diversi) si spostano dopo, una per una.
 */
export const creaSquadra = async (corsoId, squadra, passo = () => {}) => {
    const token = gettone();
    const apiClient = new MyApiClient();

    passo('Creazione della squadra…');
    const weeklySchedule = regolaSettimanaleDa(squadra.date);
    const prima = (squadra.date || [])[0] || {};

    const creata = await apiClient.createCourseTeam(token, {
        bookingCourseId: corsoId,
        name: squadra.nome,
        coachId: prima.maestroId,
        venueId: prima.impiantoId,
        weeklySchedule,
        startDate: squadra.dal,
        endDate: squadra.al,
        maxMembers: parseInt(squadra.maxMembri, 10) || 0,
        status: 'DRAFT',
    });
    if (creata?.error) return { errore: creata.error };
    const idSquadra = creata?.id || creata?.data?.id;
    if (!idSquadra) return { errore: 'il server non ha restituito la squadra creata' };

    // Se la squadra e' stata salvata come bozza ci si ferma qui: le sedute nascono
    // all'attivazione, ed e' l'operatore a decidere quando.
    if (statoSquadraServer(squadra.stato) !== 'ACTIVE') return { idSquadra, bozza: true };

    passo('Attivazione e generazione delle sedute…');
    const attivata = await apiClient.updateCourseTeam(token, idSquadra, { status: 'ACTIVE' });
    if (attivata?.error) return { errore: attivata.error, idSquadra };

    // Le eccezioni: le date che non seguono la regola piu' usata
    const conteggio = new Map();
    (squadra.date || []).forEach(d => {
        const chiave = `${d.giorno}-${d.inizio}-${d.fine}`;
        conteggio.set(chiave, (conteggio.get(chiave) || 0) + 1);
    });
    const schemaPrincipale = [...conteggio.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
    const eccezioni = (squadra.date || []).filter(d =>
        `${d.giorno}-${d.inizio}-${d.fine}` !== schemaPrincipale
        || d.maestroId !== prima.maestroId || d.impiantoId !== prima.impiantoId);

    let spostate = 0;
    let fallite = 0;
    if (eccezioni.length > 0) {
        passo(`Applico ${eccezioni.length} eccezioni…`);
        const sedute = elenco(await apiClient.getTeamSlotBookings(token, idSquadra)).map(dataDaSeduta);
        for (const d of eccezioni) {
            const seduta = sedute.find(s => s.data === d.data && s.inizio === d.inizio) || sedute.find(s => s.data === d.data);
            if (!seduta) { fallite++; continue; }
            const esito = await apiClient.rescheduleCalendarioEvento(token, seduta.id, {
                newDataInizio: `${d.data}T${d.inizio}:00`,
                newDataFine: `${d.data}T${d.fine}:00`,
                newImpiantoId: d.impiantoId,
                newStaffId: d.maestroId,
                notificaPartecipanti: false,
                notificaStaff: false,
            });
            if (esito?.error) fallite++;
            else spostate++;
        }
    }

    return { idSquadra, spostate, fallite, eccezioni: eccezioni.length };
};

/**
 * Nome, posti e stato di una squadra.
 *
 * Finche' la squadra e' rimasta in bozza non c'e' niente a calendario: da qui si scrivono
 * anche il periodo, il giorno e l'orario, il maestro e l'impianto, che diventeranno sedute
 * quando la squadra verra' attivata. Da attiva in poi quelle cose le detta il riallineamento
 * delle sedute, una alla volta, e mandarle da qui verrebbe rifiutato.
 */
export const salvaSquadra = async (squadra, periodoModificabile = false) => {
    const token = gettone();
    const apiClient = new MyApiClient();
    const prima = (squadra.date || [])[0] || {};
    const risposta = await apiClient.updateCourseTeam(token, squadra.id, {
        name: squadra.nome,
        maxMembers: parseInt(squadra.maxMembri, 10) || 0,
        status: statoSquadraServer(squadra.stato),
        ...(periodoModificabile && squadra.dal ? { startDate: squadra.dal } : {}),
        ...(periodoModificabile && squadra.al ? { endDate: squadra.al } : {}),
        ...(periodoModificabile && prima.maestroId ? { coachId: prima.maestroId } : {}),
        ...(periodoModificabile && prima.impiantoId ? { venueId: prima.impiantoId } : {}),
        ...(periodoModificabile && (squadra.date || []).length > 0
            ? { weeklySchedule: regolaSettimanaleDa(squadra.date) }
            : {}),
    });
    return motivo(risposta, 'salvataggio non riuscito');
};

/**
 * Elimina il corso. Il server lo consente in qualsiasi stato ma solo finche' il corso non ha
 * lasciato tracce: quando rifiuta, il motivo arriva da lui e va mostrato com'e'.
 */
export const eliminaCorso = async (corsoId) => {
    const token = gettone();
    const risposta = await new MyApiClient().deleteBookingCourse(token, corsoId);
    return motivo(risposta, 'eliminazione non riuscita');
};

/** Stato di una squadra già a calendario (Attiva / Non attiva): si scrive subito, da solo. */
export const cambiaStatoSquadra = async (squadraId, stato) => {
    const token = gettone();
    const risposta = await new MyApiClient().updateCourseTeam(token, squadraId, { status: statoSquadraServer(stato) });
    return motivo(risposta, 'cambio di stato non riuscito');
};

export const eliminaSquadra = async (squadraId) => {
    const token = gettone();
    const risposta = await new MyApiClient().deleteCourseTeam(token, squadraId);
    return motivo(risposta, 'eliminazione non riuscita');
};

/** Sposta una sola seduta: giorno, orario, maestro o impianto. */
export const spostaSeduta = async (seduta, avvisa = false) => {
    const token = gettone();
    const risposta = await new MyApiClient().rescheduleCalendarioEvento(token, seduta.id, {
        newDataInizio: `${seduta.data}T${seduta.inizio}:00`,
        newDataFine: `${seduta.data}T${seduta.fine}:00`,
        newImpiantoId: seduta.impiantoId || undefined,
        newStaffId: seduta.maestroId || undefined,
        notificaPartecipanti: avvisa,
        notificaStaff: avvisa,
    });
    return motivo(risposta, 'spostamento non riuscito');
};

export const eliminaSeduta = async (squadraId, sedutaId) => {
    const token = gettone();
    const risposta = await new MyApiClient().deleteTeamSession(token, squadraId, sedutaId);
    return motivo(risposta, 'eliminazione non riuscita');
};

/**
 * «Da qui in poi»: la seduta modificata detta la nuova regola per il suo giorno della settimana,
 * e il server rifa' tutte le sedute da quella data in avanti. Gli altri giorni della regola
 * restano quelli che erano.
 */
export const riallineaDaSeduta = async (squadra, seduta) => {
    const token = gettone();
    const regolaVecchia = (squadra.regola || []).filter(r => r.dayOfWeek !== seduta.giorno);
    const risposta = await new MyApiClient().realignTeamSessions(token, squadra.id, {
        fromDate: seduta.data,
        weeklySchedule: [
            ...regolaVecchia,
            { dayOfWeek: seduta.giorno, startTime: seduta.inizio, endTime: seduta.fine },
        ],
        coachId: seduta.maestroId || undefined,
        venueId: seduta.impiantoId || undefined,
    });
    if (risposta?.error) return { errore: risposta.error };
    return risposta;
};

/** Mette a calendario le date proposte dalla regola, una per una, saltando quelle che ci sono già. */
export const aggiungiSedute = async (squadraId, date, passo = () => {}) => {
    const token = gettone();
    const apiClient = new MyApiClient();
    let aggiunte = 0;
    const problemi = [];
    for (let i = 0; i < date.length; i++) {
        passo(`Aggiungo la data ${i + 1} di ${date.length}…`);
        const d = date[i];
        const esito = await apiClient.createTeamSession(token, squadraId, {
            date: d.data,
            startTime: d.inizio,
            endTime: d.fine,
            coachId: d.maestroId || undefined,
            venueId: d.impiantoId || undefined,
        });
        if (esito?.error) problemi.push(`${d.data}: ${esito.error}`);
        else aggiunte++;
    }
    return { aggiunte, problemi };
};

/** Assegna o rimuove dei partecipanti da una squadra. */
export const assegnaPartecipanti = async (partecipanti, squadraId) => {
    const token = gettone();
    const apiClient = new MyApiClient();
    const problemi = [];
    for (const p of partecipanti) {
        // Prima si esce da dove si era, poi si entra: una persona sta in una squadra sola
        if (p.squadraId && p.squadraId !== squadraId) {
            const uscita = await apiClient.removeMemberFromTeam(token, p.squadraId, p.userId);
            if (uscita?.error) { problemi.push(`${p.nome}: ${uscita.error}`); continue; }
        }
        if (squadraId && p.squadraId !== squadraId) {
            const entrata = await apiClient.addMemberToTeam(token, squadraId, { userId: p.userId });
            if (entrata?.error) problemi.push(`${p.nome}: ${entrata.error}`);
        }
    }
    return problemi;
};

/** Rimuove dal corso: l'acquisto viene annullato e la persona esce dalla sua squadra. */
export const togliDalCorso = async (partecipanti) => {
    const token = gettone();
    const apiClient = new MyApiClient();
    const problemi = [];
    for (const p of partecipanti) {
        if (p.squadraId) await apiClient.removeMemberFromTeam(token, p.squadraId, p.userId);
        const esito = await apiClient.setCancelledUserCourse(token, p.id, 'Rimosso dal corso dalla struttura');
        if (esito?.error) problemi.push(`${p.nome}: ${esito.error}`);
    }
    return problemi;
};

/**
 * Il controllo del server sulle date di una squadra ancora in bozza: orari di apertura
 * dell'impianto, disponibilità del maestro per lo sport, prenotazioni già prese. È lo stesso
 * che il server rifà quando si attiva la squadra, quindi quello che segnala qui è quello che
 * farebbe fallire l'attivazione.
 * Restituisce { motivi: { 'AAAA-MM-GG-HH:MM-HH:MM': 'motivo' }, errore }.
 */
export const controllaDateSulServer = async (date, sport) => {
    // `sport` puo' essere l'elenco degli sport del corso: il server accetta il maestro per uno qualsiasi
    const sports = Array.isArray(sport) ? sport : (sport ? [sport] : []);
    const gruppi = new Map();
    (date || []).forEach(d => {
        if (!d.data || !d.inizio || !d.fine || !d.maestroId || !d.impiantoId) return;
        const chiave = `${d.maestroId}|${d.impiantoId}`;
        if (!gruppi.has(chiave)) gruppi.set(chiave, []);
        gruppi.get(chiave).push(d);
    });
    const motivi = {};
    // Lo stesso controllo diviso per risorsa: { impianto: 'motivo' | null, maestro: 'motivo' | null }
    const dettagli = {};
    let errore = null;
    for (const elenco of gruppi.values()) {
        const giorni = elenco.map(d => d.data).sort();
        const risposta = await new MyApiClient().checkCourseConflicts(gettone(), {
            coachId: elenco[0].maestroId,
            venueId: elenco[0].impiantoId,
            startDate: giorni[0],
            endDate: giorni[giorni.length - 1],
            weeklySchedule: regolaSettimanaleDa(elenco.map(d => ({ ...d, giorno: d.giorno || giornoDellaData(d.data) }))),
            sport: sports[0],
            sports,
        });
        if (!risposta || risposta.error) { errore = (risposta && risposta.error) || 'controllo non disponibile'; continue; }
        const volute = new Set(elenco.map(d => `${d.data}-${d.inizio}-${d.fine}`));
        (risposta.slots || []).forEach(s => {
            const chiave = `${String(s.date).slice(0, 10)}-${s.startTime}-${s.endTime}`;
            if (s.isValid || !volute.has(chiave)) return;
            motivi[chiave] = [s.venueConflict?.hasConflict && s.venueConflict.reason, s.coachConflict?.hasConflict && s.coachConflict.reason]
                .filter(Boolean).join('; ') || 'Non disponibile';
            dettagli[chiave] = {
                impianto: s.venueConflict?.hasConflict ? (s.venueConflict.reason || 'Non disponibile') : null,
                maestro: s.coachConflict?.hasConflict ? (s.coachConflict.reason || 'Non disponibile') : null,
            };
        });
    }
    return { motivi, dettagli, errore };
};
